import { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { authedFetch } from "../lib/api";
import { Button } from "../components/ui/button";
import { LogOut, Check, Loader2, ArrowLeft, Sparkles, CreditCard, ExternalLink } from "lucide-react";
import { SRMark } from "../components/SRMark";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = {
  SOLO: ["Single practitioner profile", "Appear in the GP directory", "See when GP practices select you"],
  CLINIC_LIGHT: ["Up to 5 clinicians", "Team availability management", "Clinic admin dashboard", "All Individual features"],
  CLINIC_PLUS: ["Unlimited clinicians", "Priority directory placement", "Top-referrer analytics", "All Clinic Light features"],
};

const STATUS_LABEL = {
  TRIAL: { label: "Trial", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  ACTIVE: { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PAST_DUE: { label: "Past due", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  CANCELLED: { label: "Cancelled", tone: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function Subscribe() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [notAdmin, setNotAdmin] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const { data: acct } = await supabase
        .from("accounts").select("*").eq("billing_email", user.email).maybeSingle();
      if (!active) return;
      if (!acct) { setNotAdmin(true); setLoading(false); return; }
      setAccount(acct);
      try {
        const res = await fetch(`${API}/billing/plans`);
        setPlans(await res.json());
      } catch (e) { toast.error("Could not load plans."); }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    const sessionId = searchParams.get("setup_session_id");
    if (!sessionId || !user) return;
    setWorking("card");
    authedFetch(`/billing/setup-status/${sessionId}`).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Payment details could not be confirmed.");
      const { data: refreshed } = await supabase.from("accounts").select("*").eq("billing_email", user.email).maybeSingle();
      setAccount(refreshed);
      setSearchParams({}, { replace: true });
      toast.success("Payment details added securely. Nothing has been charged.");
    }).catch((error) => toast.error(error.message)).finally(() => setWorking(null));
  }, [searchParams, setSearchParams, user]);

  const selectPlan = async (planId) => {
    setWorking(planId);
    try {
      const res = await authedFetch(`/billing/select-plan`, {
        method: "POST",
        body: JSON.stringify({ plan_id: planId, billing_period: "monthly" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Plan could not be saved");
      setAccount(data.account);
      toast.success("Plan saved. Your trial start date has not changed.");
    } catch (e) {
      toast.error(e.message || "Could not save the plan.");
    } finally {
      setWorking(null);
    }
  };

  const addCard = async () => {
    setWorking("card");
    try {
      const response = await authedFetch("/billing/setup", { method: "POST", body: JSON.stringify({ origin_url: window.location.origin }) });
      const data = await response.json();
      if (!response.ok || !data.url) throw new Error(data.detail || "Could not open secure payment setup.");
      window.location.assign(data.url);
    } catch (error) { toast.error(error.message); setWorking(null); }
  };

  const manageBilling = async () => {
    setWorking("portal");
    const response = await authedFetch("/billing/portal", { method: "POST", body: JSON.stringify({ return_url: window.location.href }) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.url) { toast.error(data.detail || "Could not open billing."); setWorking(null); return; }
    window.location.assign(data.url);
  };

  if (!authLoading && !user) return <Navigate to="/login" replace />;

  const st = account ? (STATUS_LABEL[account.subscription_status] || STATUS_LABEL.TRIAL) : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            <SRMark />
            <span className="font-heading text-lg font-semibold tracking-tight text-slate-900">SimplyRefer</span>
            <span className="ml-1 rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-secondary-foreground">Billing</span>
          </div>
          <Button variant="outline" size="sm" onClick={async () => { await signOut(); navigate("/login", { replace: true }); }} data-testid="subscribe-page-logout-button">
            <LogOut className="mr-2 h-4 w-4" /> Log out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 animate-fadein" data-testid="subscribe-page">
        <button onClick={() => navigate(user?.user_metadata?.role === "clinician" ? "/clinician?tab=overview" : "/clinic?tab=overview")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="back-to-clinic-link">
          <ArrowLeft className="h-4 w-4" /> Back to my portal
        </button>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading plans…</div>
        ) : notAdmin ? (
          <p className="text-slate-600">This account isn't a clinic admin.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">Choose your plan</h1>
                <p className="mt-1 text-sm text-slate-500">{account.account_name} — simple monthly billing, no annual commitment. Your plan can be changed later.</p>
              </div>
              <div className="text-sm text-slate-600">
                Current: <span className="font-semibold" data-testid="current-tier">{account.subscription_tier}</span>{" "}
                <span className={`ml-1 inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${st.tone}`} data-testid="current-status">{st.label}</span>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
              {account.trial_started_at ? (
                <p>Your 90-day trial is active. No subscription charge is due before {new Date(account.trial_ends_at).toLocaleDateString("en-AU")}.</p>
              ) : (
                <p><strong>Your 90-day trial has not started.</strong> It begins only when SimplyRefer goes live. Adding a card is optional before launch and does not create a charge.</p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={addCard} disabled={!!working}>
                  {working === "card" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                  {account.billing_payment_method_ready ? "Replace payment card" : "Add payment card (optional)"}
                </Button>
                <Button type="button" variant="outline" onClick={manageBilling} disabled={!!working}>
                  <ExternalLink className="mr-2 h-4 w-4" /> Manage billing and invoices
                </Button>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {plans.map((p) => {
                const current = account.subscription_tier === p.tier;
                const highlight = p.id === "CLINIC_LIGHT";
                return (
                  <div key={p.id}
                    className={`relative flex flex-col rounded-lg border bg-white p-6 transition-all duration-200 ${highlight ? "border-primary shadow-md" : "border-slate-200"}`}
                    data-testid={`plan-card-${p.id}`}>
                    {highlight && (
                      <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                        <Sparkles className="h-3 w-3" /> Most popular
                      </span>
                    )}
                    <h3 className="font-heading text-xl font-semibold text-slate-900">{p.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1" data-testid={`plan-price-${p.id}`}>
                      <span className="font-heading text-4xl font-bold text-slate-900">${p.amount.toFixed(0)}</span>
                      <span className="text-sm text-slate-500">/ month {p.currency.toUpperCase()}</span>
                    </div>
                    <ul className="mt-5 space-y-2.5">
                      {(FEATURES[p.id] || []).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {f}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 flex-1" />
                    <Button
                      className="w-full"
                      variant={highlight ? "default" : "outline"}
                      disabled={!!working || current}
                      onClick={() => selectPlan(p.id)}
                      data-testid={`subscribe-button-${p.id}`}
                    >
                      {working === p.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {current ? "Selected plan" : `Choose ${p.name}`}
                    </Button>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-center text-xs text-slate-500">Secure payment details, invoices, plan changes and cancellation are handled by Stripe.</p>
          </>
        )}
      </main>
    </div>
  );
}
